import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const from = sp.get("from") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back");
      navigate(from);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-cream grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1697448524689-8dfd3d11071e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBnb2xmZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzY2NzM0Njd8MA&ixlib=rb-4.1.0&q=85"
          alt="Coach"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/30 to-forest/90" />
        <div className="absolute inset-0 p-14 flex flex-col justify-between text-cream-light">
          <Link to="/" className="font-serif text-3xl">Golf Start</Link>
          <div className="max-w-md">
            <div className="eyebrow text-gold">A Coaching Marketplace for Every Player</div>
            <div className="mt-4 font-serif text-4xl italic leading-[1.1]">
              "Whether it's your first swing or your hundredth round, find the coach to take your game further."
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden font-serif text-3xl text-forest">Golf Start</Link>
          <div className="eyebrow mt-8 lg:mt-0">Welcome back</div>
          <h1 className="mt-3 font-serif text-5xl text-forest leading-[0.95]">Sign in</h1>

          <button onClick={handleGoogle} data-testid="google-login-btn" className="mt-10 w-full border border-forest px-6 py-4 flex items-center justify-center gap-3 hover:bg-forest hover:text-cream-light transition-colors">
            <svg viewBox="0 0 24 24" className="h-4 w-4"><path fill="currentColor" d="M21.35 11.1h-9.18v2.92h5.27c-.24 1.42-1.73 4.16-5.27 4.16-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.8 0 3.02.77 3.71 1.43l2.53-2.44C16.82 3.95 14.73 3 12.17 3 6.97 3 2.76 7.21 2.76 12.42S6.97 21.83 12.17 21.83c7.02 0 9.35-4.91 9.35-7.43 0-.5-.05-.88-.17-1.3Z"/></svg>
            <span className="font-sans text-xs tracking-[0.2em] uppercase">Continue with Google</span>
          </button>

          <div className="my-8 flex items-center gap-4 text-xs text-forest-muted uppercase tracking-[0.2em]">
            <div className="flex-1 h-px bg-cream-border" />
            or with email
            <div className="flex-1 h-px bg-cream-border" />
          </div>

          <form onSubmit={submit} className="space-y-6">
            <div>
              <label className="eyebrow">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="field mt-2"
              />
            </div>
            <div>
              <label className="eyebrow">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="field mt-2"
              />
            </div>
            <button data-testid="login-submit-btn" disabled={loading} className="btn-primary w-full disabled:opacity-50">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-10 text-sm text-forest-muted">
            New to Golf Start?{" "}
            <Link to="/signup" data-testid="login-to-signup" className="text-forest underline decoration-gold underline-offset-4 hover:text-gold">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
